import { Box, Chip, ChipProps, Typography } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { TaskStatus } from 'components/core/task/styles'
import { TaskValue } from 'lib/actions/task'
import { TaskActionType, TaskState } from 'pbts/c1/api/task/v1/task'
import React, { FC } from 'react'
import { IntlShape, useIntl } from 'react-intl'

export type TaskStateProps = {
    taskView: TaskValue | null
    variant?: 'body1' | 'body2'
}

export const canMakeADecisionOn = (taskView: TaskValue): boolean =>
    canActOn(taskView, TaskActionType.APPROVE) ||
    canActOn(taskView, TaskActionType.DENY) ||
    canActOn(taskView, TaskActionType.PROVISION_ERRORED) ||
    canActOn(taskView, TaskActionType.PROVISION_COMPLETE) ||
    canActOn(taskView, TaskActionType.PROVISION_CANCELLED)

export const canActOn = (taskView: TaskValue, action: TaskActionType): boolean => {
    return taskView.actions.includes(action) || false
}

// Returns the enum string representation.
export const taskStateToString = (option: TaskState): string | undefined => {
    for (const key in TaskState) {
        // NOTE(morgabra) Hack as there's no reverse map from value -> enum key?
        if ((TaskState[key] as unknown as number) === option) {
            return key
        }
    }
    return undefined
}

// Returns the internationalized human-readable string for the given type.
export const taskStateToIntl = (state: TaskState | undefined, intl: IntlShape): string => {
    const unknown = intl.formatMessage({ id: 'Components_Tasks_State_Unknown', defaultMessage: 'Unknown' })
    if (!state) {
        return unknown
    }

    switch (state) {
        case TaskState.OPEN:
            return intl.formatMessage({ id: 'Components_Tasks_State_Open', defaultMessage: 'Open' })
        case TaskState.CLOSED:
            return intl.formatMessage({ id: 'Components_Tasks_State_Closed', defaultMessage: 'Closed' })
        default:
            return unknown
    }
}

// Returns the internationalized human-readable string for the given type as a verb for usage in sentences
export const taskStateToVerbIntl = (state: TaskState | undefined, intl: IntlShape): string => {
    const unknown = intl.formatMessage({ id: 'Components_Tasks_State_Unknown_Verb', defaultMessage: 'unknown' })
    if (!state) {
        return unknown
    }
    switch (state) {
        case TaskState.OPEN:
            return intl.formatMessage({ id: 'Components_Tasks_State_Open', defaultMessage: 'opened the request' })
        case TaskState.CLOSED:
            return intl.formatMessage({ id: 'Components_Tasks_State_Closed', defaultMessage: 'closed the request' })
        default:
            return unknown
    }
}

export const taskOpenToIntl = (state: TaskState | undefined, intl: IntlShape): string => {
    const unknown = intl.formatMessage({ id: 'Components_Tasks_State_Unknown_Verb', defaultMessage: 'unknown' })
    if (!state) {
        return unknown
    }
    switch (state) {
        case TaskState.OPEN:
            return intl.formatMessage({ id: 'Components_Task_Open_State_Open', defaultMessage: 'Open' })
        case TaskState.CLOSED:
            return intl.formatMessage({ id: 'Components_Task_Open_State_Closed', defaultMessage: 'Closed' })
        default:
            return unknown
    }
}

// { ts, date, showCopy, variant = 'body1' }: TimestampProps
export const TaskStateTypography = ({
    variant = 'body1',
    state,
    taskFormat = 'default',
}: {
    variant?: 'body1' | 'body2'
    state: TaskState | undefined
    taskFormat?: 'default' | 'verb'
}): React.ReactElement => {
    const intl = useIntl()
    return (
        <Typography display="inline" variant={variant}>
            {taskFormat === 'verb' ? taskStateToVerbIntl(state, intl) : taskStateToIntl(state, intl)}
        </Typography>
    )
}

// { ts, date, showCopy, variant = 'body1' }: TimestampProps
export const TaskStatusChip: FC<{ taskView?: TaskValue }> = ({ taskView }) => {
    const intl = useIntl()

    if (!taskView) {
        return null
    }

    const taskStateToClass = (state: TaskState): ChipProps['color'] => {
        switch (state) {
            case TaskState.OPEN:
                return 'primary'
            case TaskState.CLOSED:
            default:
                return 'info'
        }
    }

    return (
        <Box component="span" sx={TaskStatus.layout}>
            <Chip label={taskOpenToIntl(taskView?.state, intl)} color={taskStateToClass(taskView.state)} />
        </Box>
    )
}

export const EnrollmentStatusChip: FC<{ taskView?: TaskValue }> = ({ taskView }) => {
    const intl = useIntl()
    if (!taskView || taskView.state === TaskState.CLOSED) {
        return <C1EmptyIndicator />
    }

    const getEnrollmentDetails = (): ChipProps => {
        switch (taskView.type?.taskType.oneofKind) {
            case 'revoke':
                return {
                    label: intl.formatMessage({
                        id: 'enrollment_status_unenrolling',
                        defaultMessage: 'Unenrolling',
                    }),
                    color: 'primary',
                }
            case 'grant':
                return {
                    label: intl.formatMessage({
                        id: 'enrollment_status_enrolling',
                        defaultMessage: 'Enrolling',
                    }),
                    color: 'primary',
                }
            default:
                return { label: null, color: 'primary' }
        }
    }

    const chipProps = getEnrollmentDetails()

    if (!chipProps.label) {
        return <C1EmptyIndicator />
    }

    return (
        <Box component="span" sx={TaskStatus.layout}>
            <Chip {...chipProps} />
        </Box>
    )
}
